import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  Bot,
  Database,
  Dna,
  ExternalLink,
  Fingerprint,
  Gauge,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  AvatarCustomerOption,
  AvatarLabSection,
  AvatarLabSnapshot,
  AvatarProfile,
  useAdminApi,
} from '../hooks/useAdminApi';
import { buildAvatarLaunchUrl } from '../utils/avatarProfiles';

interface SuperadminAvatarLabProps {
  isDarkMode: boolean;
}

const WORKSPACE_SLUG = 'main';

const shortHash = (value?: string | null): string => value ? value.slice(0, 10) : '—';
const percent = (value?: number | null): string => value == null ? '—' : `${Math.round(value * 100)}%`;
const customerSlugOf = (customer: { slug?: string | null }): string => String(customer.slug || '').trim();
const agentSlugOf = (profile: AvatarProfile): string => String(profile.agent_slug || profile.slug || '').trim();

const profilesForCustomer = (profiles: AvatarProfile[], customerSlug: string): AvatarProfile[] => profiles
  .filter(profile => profile.status === 'published' && (
    profile.is_system
    || (profile.assigned_customers || []).some(customer => customer.slug === customerSlug)
  ))
  .sort((left, right) => Number(Boolean(right.is_system)) - Number(Boolean(left.is_system))
    || left.name.localeCompare(right.name, 'de'));

function AvatarPortrait({ snapshot }: { snapshot: AvatarLabSnapshot }) {
  const [failed, setFailed] = useState(false);
  const initials = snapshot.avatar.name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  useEffect(() => setFailed(false), [snapshot.avatar.portrait_url]);
  return (
    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-cyan-400/30 bg-slate-950 text-sm font-bold text-cyan-300 shadow-inner">
      {!failed && snapshot.avatar.portrait_url ? (
        <img src={snapshot.avatar.portrait_url} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : initials || <Bot className="h-6 w-6" aria-hidden="true" />}
      <span className="absolute inset-x-0 bottom-0 h-0.5 bg-cyan-400" />
    </div>
  );
}

function MetricBar({ label, value, identical }: { label: string; value: number | null; identical?: boolean }) {
  const numeric = value == null ? 0 : Math.round(value * 100);
  const risky = numeric >= 85;
  const color = risky ? 'bg-amber-400' : numeric >= 65 ? 'bg-cyan-400' : 'bg-emerald-400';
  return (
    <div className="min-w-0 flex-1">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</span>
        <span className={`font-mono text-sm font-bold ${risky ? 'text-amber-400' : 'text-emerald-400'}`}>
          {percent(value)}{identical ? ' = ' : ''}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-sm bg-slate-700/70">
        <div className={`h-full transition-all duration-500 ${color}`} style={{ width: `${numeric}%` }} />
      </div>
    </div>
  );
}

function PromptLayer({ section, isDarkMode }: { section: AvatarLabSection; isDarkMode: boolean }) {
  return (
    <details className={`group border-b last:border-b-0 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
      <summary className={`flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500 ${isDarkMode ? 'hover:bg-slate-800/55' : 'hover:bg-slate-50'}`}>
        <div className="min-w-0">
          <p className="text-sm font-semibold">{section.label}</p>
          <p className={`mt-0.5 truncate text-[11px] ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{section.source}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className={`font-mono text-[11px] ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{section.length.toLocaleString('de-DE')} Z.</span>
          <span className={`hidden font-mono text-[11px] sm:inline ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>{shortHash(section.sha256)}</span>
          <span className="text-slate-500 transition group-open:rotate-45">+</span>
        </div>
      </summary>
      <div className={`border-t px-4 py-4 ${isDarkMode ? 'border-slate-800 bg-slate-950/75' : 'border-slate-200 bg-slate-50'}`}>
        {section.content ? (
          <pre className={`max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{section.content}</pre>
        ) : (
          <p className="text-xs italic text-slate-500">Diese Schicht ist leer.</p>
        )}
      </div>
    </details>
  );
}

function DiagnosticLane({
  snapshot,
  customerSlug,
  isDarkMode,
}: {
  snapshot: AvatarLabSnapshot;
  customerSlug: string;
  isDarkMode: boolean;
}) {
  const panel = isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const profileForRoute = { slug: snapshot.avatar.slug, agent_slug: snapshot.avatar.agent_slug };
  const adminUrl = `/admin/${encodeURIComponent(customerSlug)}/${encodeURIComponent(snapshot.avatar.agent_slug)}?tab=preview`;
  const voiceUrl = buildAvatarLaunchUrl(customerSlug, profileForRoute);
  const metrics = [
    ['Core', snapshot.lengths.core, snapshot.fingerprints.core],
    ['Behaviour', snapshot.lengths.behavior, snapshot.fingerprints.behavior],
    ['Voice', snapshot.lengths.voice_prompt, snapshot.fingerprints.voice_prompt],
  ] as const;

  return (
    <article className={`min-w-0 overflow-hidden rounded-xl border ${panel}`}>
      <div className="relative p-5">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/80 to-transparent" />
        <div className="flex items-start gap-3">
          <AvatarPortrait snapshot={snapshot} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-lg font-semibold tracking-tight">{snapshot.avatar.name}</h3>
              <span className={`rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${snapshot.behavior.mode === 'custom' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500' : 'border-cyan-500/40 bg-cyan-500/10 text-cyan-500'}`}>
                {snapshot.behavior.mode === 'custom' ? 'Custom aktiv' : 'Blueprint aktiv'}
              </span>
            </div>
            <p className={`mt-0.5 text-sm ${muted}`}>{snapshot.avatar.role_label}</p>
            <p className={`mt-2 truncate font-mono text-[11px] ${muted}`} title={`${snapshot.scope.customer_slug} / ${snapshot.scope.workspace_slug} / ${snapshot.scope.agent_slug}`}>
              {snapshot.scope.customer_slug} / {snapshot.scope.workspace_slug} / {snapshot.scope.agent_slug}
            </p>
          </div>
        </div>

        {snapshot.behavior.custom_identical_to_blueprint ? (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            Custom ist aktiv, aber inhaltlich identisch zum Kunden-Blueprint.
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-3 divide-x divide-slate-700/50 border-y border-slate-700/50 py-3">
          {metrics.map(([label, length, hash]) => (
            <div key={label} className="min-w-0 px-3 first:pl-0 last:pr-0">
              <p className={`text-[10px] font-semibold uppercase tracking-wider ${muted}`}>{label}</p>
              <p className="mt-1 text-sm font-semibold">{length.toLocaleString('de-DE')} <span className="text-[10px] font-normal text-slate-500">Zeichen</span></p>
              <p className="mt-1 truncate font-mono text-[10px] text-cyan-500" title={hash}>{shortHash(hash)}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className={`flex items-center gap-2 text-xs ${muted}`}>
            <Database className="h-4 w-4 text-cyan-500" />
            {snapshot.knowledge.available
              ? `${snapshot.knowledge.documents} Dokumente · ${snapshot.knowledge.chunks} Chunks`
              : 'Knowledge-Zählung nicht verfügbar'}
          </div>
          <span className={`text-[11px] ${muted}`}>Blueprint Rev. {snapshot.behavior.blueprint_revision ?? '—'}</span>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <a href={adminUrl} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition ${isDarkMode ? 'border-slate-700 text-slate-200 hover:border-cyan-500 hover:text-cyan-300' : 'border-slate-300 text-slate-700 hover:border-cyan-600 hover:text-cyan-700'}`}>
            Im Admin bearbeiten <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <a href={voiceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-400">
            Voice testen <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      <div className={`border-t ${isDarkMode ? 'border-slate-800 bg-slate-950/25' : 'border-slate-200 bg-slate-50/50'}`}>
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em]">Prompt Layers</p>
            <p className={`mt-0.5 text-[11px] ${muted}`}>Inhalt nur für Superadmins sichtbar</p>
          </div>
          <Fingerprint className="h-4 w-4 text-cyan-500" />
        </div>
        <div className={isDarkMode ? 'border-t border-slate-800' : 'border-t border-slate-200'}>
          {snapshot.sections.map(section => <PromptLayer key={section.label} section={section} isDarkMode={isDarkMode} />)}
        </div>
      </div>
    </article>
  );
}

const SuperadminAvatarLab: React.FC<SuperadminAvatarLabProps> = ({ isDarkMode }) => {
  const { getSuperadminAvatars, getSuperadminAvatarLab } = useAdminApi();
  const [profiles, setProfiles] = useState<AvatarProfile[]>([]);
  const [customers, setCustomers] = useState<AvatarCustomerOption[]>([]);
  const [customerSlug, setCustomerSlug] = useState('');
  const [leftSlug, setLeftSlug] = useState('');
  const [rightSlug, setRightSlug] = useState('');
  const [data, setData] = useState<Awaited<ReturnType<typeof getSuperadminAvatarLab>>>(null);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [loadError, setLoadError] = useState('');
  const snapshotRequestId = useRef(0);

  const availableProfiles = useMemo(
    () => profilesForCustomer(profiles, customerSlug),
    [profiles, customerSlug],
  );

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    setLoadError('');
    const result = await getSuperadminAvatars();
    if (!result) {
      setLoadError('Kunden und Avatar-Zuordnungen konnten nicht geladen werden.');
      setLoadingCatalog(false);
      return;
    }
    const activeCustomers = result.customers.filter(customer => customer.status !== 'disabled');
    setProfiles(result.profiles);
    setCustomers(activeCustomers);
    setCustomerSlug(current => current && activeCustomers.some(customer => customer.slug === current)
      ? current
      : customerSlugOf(activeCustomers[0] || {}));
    setLoadingCatalog(false);
  }, [getSuperadminAvatars]);

  useEffect(() => { void loadCatalog(); }, [loadCatalog]);

  useEffect(() => {
    if (!customerSlug || availableProfiles.length === 0) {
      setLeftSlug('');
      setRightSlug('');
      setData(null);
      return;
    }
    const slugs = availableProfiles.map(agentSlugOf);
    const custom = availableProfiles.find(profile => !profile.is_system);
    setLeftSlug(current => slugs.includes(current) ? current : agentSlugOf(custom || availableProfiles[0]));
  }, [availableProfiles, customerSlug]);

  useEffect(() => {
    if (!leftSlug) return;
    const comparisonProfiles = availableProfiles.filter(profile => agentSlugOf(profile) !== leftSlug);
    const sally = comparisonProfiles.find(profile => profile.is_system || agentSlugOf(profile) === 'sally');
    setRightSlug(current => {
      if (comparisonProfiles.some(profile => agentSlugOf(profile) === current)) return current;
      const fallback = sally || comparisonProfiles[0];
      return fallback ? agentSlugOf(fallback) : '';
    });
  }, [availableProfiles, leftSlug]);

  const loadSnapshot = useCallback(async () => {
    const requestId = snapshotRequestId.current + 1;
    snapshotRequestId.current = requestId;
    if (!customerSlug || !leftSlug) {
      setLoadingSnapshot(false);
      return;
    }
    setLoadingSnapshot(true);
    setLoadError('');
    const comparisonSlug = rightSlug && rightSlug !== leftSlug ? rightSlug : undefined;
    const result = await getSuperadminAvatarLab(customerSlug, WORKSPACE_SLUG, leftSlug, comparisonSlug);
    if (snapshotRequestId.current !== requestId) return;
    if (!result) setLoadError('Die Prompt-Diagnose konnte nicht erstellt werden. Prüfe die Avatar-Zuordnung und versuche es erneut.');
    else setData(result);
    setLoadingSnapshot(false);
  }, [customerSlug, getSuperadminAvatarLab, leftSlug, rightSlug]);

  useEffect(() => { void loadSnapshot(); }, [loadSnapshot]);

  const panel = isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const field = isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100 focus:border-cyan-400' : 'border-slate-300 bg-white text-slate-900 focus:border-cyan-600';
  const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const comparison = data?.comparison;
  const behaviorSimilarity = comparison?.behavior_similarity ?? null;
  const behaviorRisk = behaviorSimilarity != null && behaviorSimilarity >= 0.85;
  const verdictRisk = Boolean(comparison?.similarity_warning);
  const verdict = behaviorSimilarity == null
    ? 'Einzelanalyse aktiv'
    : comparison?.identical_hashes?.behavior
      ? 'Behaviour identisch'
      : behaviorRisk
        ? 'Sehr ähnliche Wirkung wahrscheinlich'
        : verdictRisk
          ? 'Mindestens eine Prompt-Schicht stark ähnlich'
        : 'Eigenständige Wirkung wahrscheinlich';

  if (loadingCatalog && customers.length === 0) {
    return (
      <section className={`flex min-h-[440px] items-center justify-center rounded-xl border ${panel}`} role="status">
        <div className="text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-cyan-500" /><p className={`mt-3 text-sm ${muted}`}>Observatorium wird kalibriert…</p></div>
      </section>
    );
  }

  return (
    <section aria-label="Avatar Lab" className="space-y-5">
      <header className={`relative overflow-hidden rounded-xl border p-5 sm:p-6 ${panel}`}>
        <div className="pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full border border-cyan-400/10 bg-cyan-500/5" />
        <div className="relative flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-cyan-500"><Activity className="h-4 w-4" /><p className="text-[11px] font-bold uppercase tracking-[0.22em]">Prompt Observatory</p></div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">Avatar Lab</h2>
            <p className={`mt-2 text-sm leading-relaxed ${muted}`}>Vergleiche die tatsächlich wirksame Voice-DNA – nicht nur gespeicherte Textdateien. Alle Messungen sind read-only und explizit auf Kunde, Workspace und Agent begrenzt.</p>
            <p className={`mt-3 font-mono text-[11px] ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
              {customerSlug || '—'} / {WORKSPACE_SLUG} / {leftSlug || '—'}
            </p>
          </div>
          <div className="grid w-full gap-3 sm:grid-cols-2 xl:w-auto xl:min-w-[690px] xl:grid-cols-[1fr_1fr_1fr_auto]">
            <label className="block"><span className={`mb-1.5 block text-[10px] font-semibold uppercase tracking-wider ${muted}`}>Kunde</span><select value={customerSlug} onChange={event => setCustomerSlug(event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none ${field}`}>{customers.map(customer => <option key={customer.id || customer.slug} value={customer.slug}>{customer.name || customer.slug}</option>)}</select></label>
            <label className="block"><span className={`mb-1.5 block text-[10px] font-semibold uppercase tracking-wider ${muted}`}>Avatar A</span><select value={leftSlug} onChange={event => setLeftSlug(event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none ${field}`}>{availableProfiles.map(profile => <option key={agentSlugOf(profile)} value={agentSlugOf(profile)}>{profile.name}</option>)}</select></label>
            <label className="block"><span className={`mb-1.5 block text-[10px] font-semibold uppercase tracking-wider ${muted}`}>Vergleich</span><select value={rightSlug} onChange={event => setRightSlug(event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none ${field}`}><option value="">Kein Vergleich</option>{availableProfiles.filter(profile => agentSlugOf(profile) !== leftSlug).map(profile => <option key={agentSlugOf(profile)} value={agentSlugOf(profile)}>{profile.name}</option>)}</select></label>
            <button type="button" onClick={() => void loadSnapshot()} disabled={loadingSnapshot || !leftSlug} className={`mt-auto inline-flex h-10 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold transition disabled:opacity-50 ${isDarkMode ? 'border-slate-700 hover:border-cyan-500' : 'border-slate-300 hover:border-cyan-600'}`}><RefreshCw className={`h-4 w-4 ${loadingSnapshot ? 'animate-spin' : ''}`} /><span className="xl:sr-only">Aktualisieren</span></button>
          </div>
        </div>
      </header>

      {loadError ? (
        <div className={`flex items-start justify-between gap-4 rounded-xl border p-4 text-sm ${isDarkMode ? 'border-red-900/60 bg-red-950/25 text-red-200' : 'border-red-200 bg-red-50 text-red-800'}`}><div className="flex gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{loadError}</span></div><button type="button" onClick={() => void loadCatalog()} className="shrink-0 font-semibold underline">Neu laden</button></div>
      ) : null}

      {availableProfiles.length === 0 && !loadingCatalog ? (
        <div className={`rounded-xl border p-10 text-center ${panel}`}><Bot className="mx-auto h-8 w-8 text-slate-500" /><h3 className="mt-3 font-semibold">Keine veröffentlichten Avatare zugeordnet</h3><p className={`mx-auto mt-1 max-w-lg text-sm ${muted}`}>Ordne dem Kunden im Reiter „Avatars“ zuerst mindestens einen Avatar zu. Sally bleibt als Systemavatar verfügbar, sobald der Kundenscope existiert.</p></div>
      ) : null}

      {data ? (
        <>
          <section className={`relative overflow-hidden rounded-xl border p-5 ${panel}`} aria-label="Ähnlichkeitssignal">
            <div className={`absolute inset-y-0 left-0 w-1 ${verdictRisk ? 'bg-amber-400' : 'bg-emerald-400'}`} />
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
              <div className="flex min-w-[240px] items-start gap-3">
                <div className={`rounded-lg p-2 ${verdictRisk ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}><Gauge className="h-5 w-5" /></div>
                <div><p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${muted}`}>Similarity Signal</p><h3 className={`mt-1 text-base font-semibold ${verdictRisk ? 'text-amber-400' : 'text-emerald-500'}`}>{verdict}</h3></div>
              </div>
              <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:gap-6">
                <MetricBar label="Core" value={comparison?.core_similarity ?? null} identical={comparison?.identical_hashes?.core} />
                <MetricBar label="Behaviour" value={comparison?.behavior_similarity ?? null} identical={comparison?.identical_hashes?.behavior} />
                <MetricBar label="Voice" value={comparison?.voice_similarity ?? null} identical={comparison?.identical_hashes?.voice_prompt} />
              </div>
            </div>
            <div className={`mt-4 flex items-start gap-2 border-t pt-3 text-xs ${isDarkMode ? 'border-slate-800 text-slate-400' : 'border-slate-200 text-slate-600'}`}><Database className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-500" /><span>Die Knowledgebase verändert verfügbare Fakten, aber nicht automatisch Persönlichkeit oder Gesprächsführung. Dafür sind Core, Behaviour und Avatar-Overlay entscheidend.</span></div>
          </section>

          <div className={`grid gap-5 ${data.right ? 'xl:grid-cols-2' : 'max-w-4xl'}`}>
            <DiagnosticLane snapshot={data.left} customerSlug={customerSlug} isDarkMode={isDarkMode} />
            {data.right ? <DiagnosticLane snapshot={data.right} customerSlug={customerSlug} isDarkMode={isDarkMode} /> : null}
          </div>
        </>
      ) : loadingSnapshot ? (
        <div className="grid gap-5 xl:grid-cols-2" role="status" aria-label="Prompt-Diagnose wird geladen">
          {[0, 1].map(item => <div key={item} className={`h-96 animate-pulse rounded-xl border ${isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-slate-100'}`} />)}
        </div>
      ) : null}

      <footer className={`flex items-center gap-2 text-[11px] ${muted}`}><Dna className="h-3.5 w-3.5 text-cyan-500" />Read-only Diagnose · keine Prompt-Mutation · kein globaler Admin-Scope-Wechsel</footer>
    </section>
  );
};

export default SuperadminAvatarLab;
